// import type { ISelectedDays } from '@/interfaces/agency/rota';
import { DateTime } from 'luxon';

import type { LocalDisplayFields } from '@/interfaces/agency/schedule';
import type { IAdditionalSettings } from '@/interfaces/agency/settings/agency/additional-settings';
import type { IRate, IRateInput } from '@/interfaces/agency/settings/finance/charge-rate';

// import { toast } from 'vue3-toastify';

/* ------------------------------ DATE HELPERS ------------------------------ */

// export function formatDay(value?: string) {
//   if (value) {
//     const dt = DateTime.fromISO(value);
//     return dt.isValid ? dt.toFormat('dd') : '';
//   }
//   return '';
// }

export function formatWeekdayShort(value?: string) {
  if (value) {
    const zone = getAgencyTimeZone();
    const dt = DateTime.fromISO(value, { zone: 'utc' }).setZone(zone);

    return dt.isValid ? dt.toFormat('ccc') : '';
  }

  return '';
}

// export function formatWeekDay(value: string) {
//   if (value) {
//     const dt = DateTime.fromISO(value);
//     return dt.isValid ? dt.toFormat('cccc') : '';
//   }
//   return '';
// }

// export function formatMonth(value: string) {
//   if (value) {
//     const dt = DateTime.fromISO(value);
//     return dt.isValid ? dt.toFormat('MM') : '';
//   }
//   return '';
// }

// export function formatYear(value: string) {
//   if (value) {
//     const dt = DateTime.fromISO(value);
//     return dt.isValid ? dt.toFormat('yyyy') : '';
//   }
//   return '';
// }

export function formatDate(value: string | null, inputFormat = 'yyyy-MM-dd') {
  const dateFormat = localStorage.getItem('dateFormat') || 'dd-MM-yyyy';

  if (value) {
    const dt = DateTime.fromFormat(value, inputFormat);

    return dt.isValid ? dt.toFormat(dateFormat) : '';
  }

  return '';
}

export function formatDateFromISO(value: string | null) {
  const dateFormat = localStorage.getItem('dateFormat') || 'dd-MM-yyyy';

  if (value) {
    const dt = DateTime.fromISO(value);

    return dt.isValid ? dt.toFormat(dateFormat) : '';
  }

  return '';
}

export function formatWeekRange(week: number, year: number): string {
  const dateFormat = localStorage.getItem('dateFormat') || 'dd-MM-yyyy';

  const startOfWeek = DateTime.fromObject({ weekYear: year, weekNumber: week }).startOf('week');
  const endOfWeek = startOfWeek.endOf('week');

  return `${startOfWeek.toFormat(dateFormat)} to ${endOfWeek.toFormat(dateFormat)}`;
}

// export function payloadDateFormat(value: string | null) {
//   if (value) {
//     const dt = DateTime.fromFormat(value, 'dd/MM/yyyy');
//     if (dt.isValid) return dt.toFormat("yyyy-MM-dd'T'HH:mm:ss");
//   }
//   return value;
// }

// /* ------------------------------ TIME HELPERS ------------------------------ */

export function formatTime(value: string) {
  if (!value) return '';

  const timeFormatSetting = JSON.parse(localStorage.getItem('timeFormat') || '{}');
  const displayFormat = timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm a';

  let parsed: DateTime | null = null;

  // Try different formats gracefully
  const formatsToTry = ['HH:mm:ss', 'HH:mm', 'hh:mm a', "yyyy-MM-dd'T'HH:mm:ss", 'yyyy-MM-dd HH:mm:ss'];

  for (const fmt of formatsToTry) {
    const candidate = DateTime.fromFormat(value, fmt);
    if (candidate.isValid) {
      parsed = candidate;
      break;
    }
  }

  // If still not valid, try ISO parse
  if (!parsed || !parsed.isValid) parsed = DateTime.fromISO(value);

  if (!parsed.isValid) return '';

  return parsed.toFormat(displayFormat);
}

// Parses "1 Hrs 10 Mins" / "1 Hrs" / "30 Mins" style durations into total minutes for numeric sorting.
export function parseDurationToMinutes(formatted?: string | null): number {
  if (!formatted) return 0;

  const hoursMatch = formatted.match(/(\d+)\s*Hrs?/i);
  const minsMatch = formatted.match(/(\d+)\s*Mins?/i);

  return Number(hoursMatch?.[1] ?? 0) * 60 + Number(minsMatch?.[1] ?? 0);
}

// export function formatUTCTimeZone(value: string) {
//   const dt = DateTime.fromFormat(value, 'HH:mm:ssZ', { zone: 'utc' });
//   return dt.isValid ? dt.toFormat('HH:mm') : '';
// }

// export function payloadTimeFormat(value: string | null) {
//   if (value) {
//     const dt = DateTime.fromFormat(value, 'HH:mm');
//     return dt.isValid ? dt.toFormat('HH:mm:ss') : value;
//   }
//   return null;
// }

// export function formatDateToTime(value: string, formatted = false) {
//   const dt = DateTime.fromISO(value);
//   return dt.isValid ? dt.toFormat(formatted ? 'hh:mm a' : 'HH:mm') : '';
// }

// export function formatUTCDateToTime(value: string) {
//   const timeFormatSetting = JSON.parse(localStorage.getItem('timeFormat') || '{}');
//   const timeFormat = timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm a';

//   const dt = DateTime.fromISO(value, { zone: 'utc' });
//   return dt.isValid ? dt.toFormat(timeFormat) : '';
// }

// export function formatUTCFullTimeZone(value: string | null) {
//   const timeFormatSetting = JSON.parse(localStorage.getItem('timeFormat') || '{}');
//   const timeFormat = timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm a';

//   if (value) {
//     const dt = DateTime.fromISO(value, { zone: 'utc' });
//     return dt.isValid ? dt.toFormat(timeFormat) : '';
//   }
//   return '';
// }

// export function formatLocale(value: string) {
//   const dt = DateTime.fromISO(value);
//   return dt.isValid ? dt.toLocaleString(DateTime.DATETIME_MED) : '';
// }

// /* --------------------------- TIME CONVERSION ------------------------------ */

export const formatTime12To24 = (value: string | null) => {
  if (!value) return '';
  const timeFormatSetting = JSON.parse(localStorage.getItem('timeFormat') || '{}');
  const timeFormat = timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm a';

  const dt = DateTime.fromFormat(value, timeFormat);

  return dt.isValid ? dt.toFormat('HH:mm:ss') : '';
};

export const formatTime24To12 = (value: string | null) => {
  if (!value) return '';
  const dt = DateTime.fromFormat(value, 'HH:mm');

  return dt.isValid ? dt.toFormat('hh:mm a') : '';
};

// export const formatShiftDate = (date: string, days: number) => {
//   if (!date) return '';
//   const dt = DateTime.fromFormat(date, 'yyyy-MM-dd');
//   return dt.isValid ? dt.plus({ days }).toFormat('yyyy-MM-dd') : '';
// };

// /* --------------------------- COMBINED FORMATS ----------------------------- */

// export function returnJoinDates(startDate: string, endDate: string) {
//   let result = formatDate(startDate);
//   if (endDate) result = `${result} - ${formatDate(endDate)}`;
//   return result;
// }

// export function returnJoinTimes(startTime: string, endTime: string) {
//   let result = formatTime(startTime);
//   if (endTime) result = `${result} - ${formatTime(endTime)}`;
//   return result;
// }

// export function formatDateToString(date?: string) {
//   if (!date) return '';
//   const dt = DateTime.fromISO(date);
//   return dt.isValid ? dt.toFormat('cccc - dd LLL, yyyy') : '';
// }

// /* ------------------------------ ROTA & AGE ------------------------------- */

// export function formatRotaDay(payload: ISelectedDays | undefined) {
//   const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
//   const orderedDays = days.filter(day => Object.prototype.hasOwnProperty.call(payload, day));
//   if (orderedDays.length === days.length) return 'All Days';
//   return orderedDays.join(', ');
// }

// export const calculateAge = (value: string, inputFormat = 'yyyy-MM-dd') => {
//   if (value) {
//     const dob = DateTime.fromFormat(value, inputFormat);
//     if (!dob.isValid) return '';
//     const diff = DateTime.now().diff(dob, ['years', 'months']).toObject();
//     const years = Math.floor(diff.years || 0);
//     const months = Math.floor(diff.months || 0);
//     return years > 0 ? `${years} year${years > 1 ? 's' : ''}` : `${months} month${months > 1 ? 's' : ''}`;
//   }
// };

// /* --------------------------- TEXT UTILITIES ------------------------------- */

// export const formatCamelCase = (value: string) =>
//   value.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

// export const formatTitleCase = (value: string) =>
//   value ? value.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) : '';

// export const truncateText = (value: string, length = 30) =>
//   value ? (value.length > length ? value.substring(0, length) + '...' : value) : '';

// export const capitalizeFirstWord = (text: string) => {
//   if (!text) return '';
//   text = text.trim();
//   return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
// };

// /* --------------------------- IMAGE UPLOAD UTILS --------------------------- */

// export function useImageUpload() {
//   const isImageCropperDialogVisible = ref(false);
//   const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
//   const imageData = ref<{ file: File | null; name: string; type: string }>({
//     file: null,
//     name: '',
//     type: ''
//   });

//   const handleImageUpload = (event: Event) => {
//     const target = event.target as HTMLInputElement;
//     if (target.files && target.files[0]) {
//       const file = target.files[0];

//       if (!allowedImageTypes.includes(file.type)) {
//         toast.error('Invalid image format, Please upload a JPEG, PNG, or WEBP.');
//         return;
//       }

//       imageData.value = { file, name: file.name, type: file.type };
//       isImageCropperDialogVisible.value = true;
//     }
//   };

//   const uploadImage = () => {
//     const input = document.createElement('input');
//     input.type = 'file';
//     input.accept = 'image/*';
//     input.addEventListener('change', handleImageUpload);
//     input.click();
//   };

//   return {
//     imageData,
//     isImageCropperDialogVisible,
//     uploadImage
//   };
// }

// /* ----------------------------- MISC HELPERS ------------------------------- */

// export const resolveUserStatusVariant = (value: string) => {
//   if (value) {
//     const stat = value.toLowerCase();
//     if (stat === 'pending') return 'warning';
//     if (stat === 'active') return 'success';
//     if (stat === 'inactive') return 'error';
//     if (stat === 'not verified') return 'info';
//     if (stat === 'unavailable') return 'grey';
//     return 'primary';
//   }
// };

// export const getTableHeight = (offset: number = 0): number => {
//   return window.innerHeight - offset;
// };

// export const tableHeight = {
//   separateFilter: () => getTableHeight(260),
//   withFilter: () => getTableHeight(242),
//   withFilterSubMenu: () => getTableHeight(288),
//   withOverviewMenu: () => getTableHeight(198),
//   separateMenuWithFilter: () => getTableHeight(328),
//   separateMenuWithFilterSubMenu: () => getTableHeight(373)
// };

// /* -------------------------- VISIBLE ITEMS UTILITY ------------------------- */

// export const useVisibleItems = <T extends { id: number; [key: string]: any }>(
//   source: ComputedRef<T[] | undefined>,
//   selectedItems: ComputedRef<unknown[]>,
//   maxVisible = 1,
//   filterField = 'id'
// ) => {
//   return computed(() => {
//     const items = source.value ?? [];
//     const selected = selectedItems.value ?? [];

//     if (items.length === 0 || selected.length === 0) {
//       return { visible: [], remaining: 0 };
//     }

//     const selectedObjects =
//       filterField === 'id'
//         ? items.filter(i => selected.includes(i.id))
//         : items.filter(i =>
//             selected.some(s => (s as Record<string, any>)[filterField] === i[filterField as keyof typeof i])
//           );

//     const totalSelected = selectedObjects.length;

//     if (totalSelected === 0) {
//       return { visible: [], remaining: 0 };
//     }

//     const visible =
//       totalSelected <= maxVisible
//         ? selectedObjects.map(i => i.id)
//         : selectedObjects.slice(0, maxVisible).map(i => i.id);

//     return {
//       visible,
//       remaining: Math.max(0, totalSelected - maxVisible)
//     };
//   });
// };

// /* --------------------------- WEEKDAY ORDER SORT --------------------------- */

// export const weekdayOrder: Record<string, number> = {
//   Monday: 1,
//   Tuesday: 2,
//   Wednesday: 3,
//   Thursday: 4,
//   Friday: 5,
//   Saturday: 6,
//   Sunday: 7
// };

// export const sortWeekdays = (days: string[]): string[] => [...days].sort((a, b) => weekdayOrder[a] - weekdayOrder[b]);

/* --------------------------- Testing Timezone --------------------------- */

/**
 * Converts a UTC datetime string to user's preferred timezone
 * and formats with both date and time.
 */
export function convertUTCToUserDateTime(utcString: string | null): string {
  if (!utcString) return '';

  const timeZone = localStorage.getItem('timeZone') || 'UTC';
  const dateFormat = localStorage.getItem('dateFormat') || 'dd-MM-yyyy';
  const timeFormatSetting = JSON.parse(localStorage.getItem('timeFormat') || '{}');
  const timeFormat = timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm a';

  const format = `${dateFormat} ${timeFormat}`;

  return DateTime.fromISO(utcString, { zone: 'utc' }).setZone(timeZone).toFormat(format);
}

/**
 * Converts a UTC datetime string to user's preferred timezone
 * and returns only the formatted date.
 */
export function convertUTCToUserDate(utcString: string): string {
  if (!utcString) return '';

  const timeZone = localStorage.getItem('timeZone') || 'UTC';
  const dateFormat = localStorage.getItem('dateFormat') || 'dd-MM-yyyy';

  return DateTime.fromISO(utcString, { zone: 'utc' }).setZone(timeZone).toFormat(dateFormat);
}

/**
 * Converts a UTC datetime string to user's preferred timezone
 * and returns only the formatted time.
 */
// export function convertUTCToUserTime(utcString: string | null): string {
//   if (!utcString) return '';

//   const timeZone = localStorage.getItem('timeZone') || 'UTC';
//   const timeFormatSetting = JSON.parse(localStorage.getItem('timeFormat') || '{}');
//   const timeFormat = timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm a';

//   return DateTime.fromISO(utcString, { zone: 'utc' }).setZone(timeZone).toFormat(timeFormat);
// }

// Called per visit card on every render of the schedule, so results are memoised. The key includes the
// zone and format, so a changed agency time zone / 12h-24h setting still produces fresh values.
const userTimeCache = new Map<string, string>();
const USER_TIME_CACHE_LIMIT = 5000;

export function convertUTCToUserTime(utcString: string | null): string {
  if (!utcString) return '';

  const timeZone = localStorage.getItem('timeZone') || 'UTC';
  const timeFormatRaw = localStorage.getItem('timeFormat') || '{}';
  const cacheKey = `${timeZone}|${timeFormatRaw}|${utcString}`;
  const cached = userTimeCache.get(cacheKey);

  if (cached !== undefined) return cached;

  const timeFormatSetting = JSON.parse(timeFormatRaw);
  const timeFormat = timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm a';
  const result = DateTime.fromISO(utcString, { zone: 'utc' }).setZone(timeZone).toFormat(timeFormat);

  if (userTimeCache.size >= USER_TIME_CACHE_LIMIT) userTimeCache.clear();
  userTimeCache.set(cacheKey, result);

  return result;
}

export function convertUTCToUserTime24(utcString: string | null): string {
  if (!utcString) return '';

  const timeZone = localStorage.getItem('timeZone') || 'UTC';
  const timeFormat = 'HH:mm:ss';

  return DateTime.fromISO(utcString, { zone: 'utc' }).setZone(timeZone).toFormat(timeFormat);
}

export function convertToUserTime(timeString: string | null): string {
  if (!timeString) return '';

  const timeFormatSetting = JSON.parse(localStorage.getItem('timeFormat') || '{}');
  const timeFormat = timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm a';

  const dt = DateTime.fromISO(timeString);

  return dt.toFormat(timeFormat);
}

/**
 * Convert UTC datetime string to user’s preferred timezone
 * for displaying in TimePicker or UI.
 */
export function convertUTCToUserTimeOnly(utcString: string): string {
  if (!utcString) return '';
  const timeZone = localStorage.getItem('timeZone') || 'UTC';
  const { h24 } = JSON.parse(localStorage.getItem('timeFormat') || '{"h24": false}');
  const userTime = DateTime.fromISO(utcString, { zone: 'utc' }).setZone(timeZone);

  return userTime.toFormat(h24 ? 'HH:mm' : 'hh:mm a');
}

/**
 * Min/max time (HH:mm) in user timezone for custom time picker:
 * [appointmentStart - thresholdHours, appointmentEnd + thresholdHours].
 */
export function getCustomTimeBounds(
  utcStart: string,
  utcEnd: string,
  thresholdHours: number
): { minTime: string; maxTime: string } | null {
  if (!utcStart || !utcEnd) return null;
  const startLocal = parseUTCToLocal(utcStart);
  const endLocal = parseUTCToLocal(utcEnd);
  if (!startLocal.isValid || !endLocal.isValid) return null;

  return {
    minTime: startLocal.minus({ hours: thresholdHours }).toFormat('HH:mm'),
    maxTime: endLocal.plus({ hours: thresholdHours }).toFormat('HH:mm')
  };
}

/**
 * Get agency's time zone (for display and layout). Prefer agencyProfile.timeZone when set.
 */
export function getAgencyTimeZone(): string {
  const profile = JSON.parse(localStorage.getItem('agencyProfile') || '{}');

  return profile?.timeZone || localStorage.getItem('timeZone') || 'UTC';
}

/**
 * Auto-charge only kicks in when the "late cancellations" toggle is on AND the appointment start
 * is within the configured hours window from now — i.e. cancelling with less notice than the
 * window allows gets auto-charged; cancelling outside the window does not. Both sides are
 * compared in the agency's IANA time zone so Luxon resolves the correct UTC offset for that
 * instant (BST vs GMT etc.) without any manual DST handling.
 *
 * autoChargeHoursWindow reads the hours from its value.key, e.g. value: { key: "24" }.
 */
export function isInsideAutoChargeHoursWindow(
  additionalSettings: IAdditionalSettings[],
  appointmentStartUtc: string | null | undefined
): boolean {
  const lateCancellationSetting = additionalSettings.find(s => s.key === 'auto_charge_late_cancellations');
  const windowSetting = additionalSettings.find(s => s.key === 'auto_charge_hours_window');

  const isLateCancellationEnabled = !!lateCancellationSetting?.value?.key;
  const hoursWindow = Number(windowSetting?.value?.key);

  if (!isLateCancellationEnabled || !Number.isFinite(hoursWindow) || hoursWindow <= 0) return false;
  if (!appointmentStartUtc) return false;

  const agencyTimeZone = getAgencyTimeZone();

  const now = DateTime.now().setZone(agencyTimeZone);
  const appointmentStart = DateTime.fromISO(appointmentStartUtc, { zone: 'utc' }).setZone(agencyTimeZone);

  if (!appointmentStart.isValid) return false;

  const hoursUntilAppointment = Math.abs(appointmentStart.diff(now, 'hours').hours);

  return hoursUntilAppointment <= hoursWindow;
}

/**
 * Confirmation text for reverting a visit / carer cancellation — shared by the schedule list row
 * action and the visit details dialog so the wording can't drift between them.
 */
export const getRevertCancellationConfirmMessage = (carerName?: string | null): string => {
  const subject = carerName ? `${carerName}'s cancellation on this visit` : 'the cancellation of this visit';

  return (
    `Are you sure you want to revert ${subject}? ` +
    'Any pending timesheet generated for the cancellation will be removed. ' +
    'This cannot be done if that timesheet has already been approved.'
  );
};

/**
 * A carer who was clocked in and already has real clinical work recorded against them (a
 * completed/missed task, or a logged medication event) shouldn't be offered as cancellable —
 * cancelling them would discard that record. A carer who is merely 'cancelled' or 'open' (never
 * clocked in) doesn't count here, even if a stray medication/task record names them. Returns the
 * set of carerIds that were clocked in AND have at least one such completed medication or task,
 * matching the same "completed" checks used by Medication.vue/DailyTask.vue.
 */
export function getCarerIdsWithCompletedClinicalWork(
  medications: Array<{ carerId?: number | null; eventType?: string | null; eventReason?: string | null }> = [],
  tasks: Array<{ carerId?: number | null; status?: string | null }> = [],
  timeclocks: Array<{ carerId?: number | null; clockInTime?: string | null }> = []
): Set<number> {
  const clockedInCarerIds = new Set<number>();

  timeclocks.forEach(tc => {
    if (tc.carerId && tc.clockInTime) {
      clockedInCarerIds.add(tc.carerId);
    }
  });

  const carerIds = new Set<number>();

  medications.forEach(med => {
    if (med.carerId && clockedInCarerIds.has(med.carerId) && med.eventType != null && med.eventReason != null) {
      carerIds.add(med.carerId);
    }
  });

  tasks.forEach(task => {
    const isCompleted = task.status === 'completed' || task.status === 'missed';

    if (task.carerId && clockedInCarerIds.has(task.carerId) && isCompleted) {
      carerIds.add(task.carerId);
    }
  });

  return carerIds;
}

/**
 * Return UTC date range { startDate, endDate } (yyyy-MM-dd) so that any appointment
 * falling on viewedDate in the user's timezone is included in the backend result.
 * Use this when building API query params for schedule endpoints.
 */
export function getUtcDateRangeForViewedDay(
  viewedDateYMD: string,
  timeZone?: string
): { startDate: string; endDate: string } {
  if (!viewedDateYMD) {
    const today = DateTime.now()
      .setZone(timeZone || getAgencyTimeZone())
      .toFormat('yyyy-MM-dd');

    return { startDate: today, endDate: today };
  }

  return {
    startDate: viewedDateYMD,
    endDate: viewedDateYMD
  };
}

/**
 * Parse a UTC datetime string (ISO or SQL) and return the same moment in the user's timezone.
 * Used for all timeline layout and overlap logic so display is based on local time.
 */
export function parseUTCToLocal(utcString: string | null | undefined): DateTime {
  if (!utcString) return DateTime.invalid('missing');
  const zone = getAgencyTimeZone();
  let dt = DateTime.fromISO(utcString, { zone: 'utc' });
  if (!dt.isValid) dt = DateTime.fromSQL(utcString, { zone: 'utc' });

  return dt.isValid ? dt.setZone(zone) : dt;
}

const TOTAL_MINUTES_PER_DAY = 24 * 60;

/**
 * Compute timeline display fields (left %, width %, startMatch) from UTC start/end,
 * in the user's timezone, for a single viewed day.
 * Returns null if the appointment does not fall on viewedDate in user TZ.
 * Handles overnight: clamps to [viewedDayStart, viewedDayEnd] and uses that segment for layout.
 */
export function localDisplayFieldsForDay(
  utcStart: string,
  utcEnd: string,
  viewedDateYMD: string,
  timeSlots: string[]
): LocalDisplayFields | null {
  const startLocal = parseUTCToLocal(utcStart);
  const endLocal = parseUTCToLocal(utcEnd);
  if (!startLocal.isValid || !endLocal.isValid) return null;

  const viewedStart = DateTime.fromFormat(viewedDateYMD, 'yyyy-MM-dd', { zone: startLocal.zone }).startOf('day');
  const viewedEnd = viewedStart.endOf('day');

  if (endLocal <= viewedStart || startLocal >= viewedEnd) return null;

  const clampStart = startLocal < viewedStart ? viewedStart : startLocal;
  const clampEnd = endLocal > viewedEnd ? viewedEnd : endLocal;

  const startMinutes = clampStart.hour * 60 + clampStart.minute;
  const endMinutes = clampEnd.hour * 60 + clampEnd.minute;
  const durationMinutes = endMinutes - startMinutes;
  if (durationMinutes <= 0) return null;

  const left = (startMinutes / TOTAL_MINUTES_PER_DAY) * 100;
  const width = (durationMinutes / TOTAL_MINUTES_PER_DAY) * 100;

  const startMatchIndex = Math.min(Math.floor(startMinutes / 30), Math.max(0, timeSlots.length - 1));

  const startHHmm = clampStart.toFormat('HH:mm');
  const endHHmm = clampEnd.toFormat('HH:mm');

  return {
    start: startHHmm,
    end: endHHmm,
    left,
    width,
    startMatchIndex,
    localStartDate: clampStart.toFormat('yyyy-MM-dd')
  };
}

/** Normalizes "HH:mm" (5 chars) to "HH:mm:ss" for date-time parsing. */
export function normalizeTimeToHHmmss(time: string): string {
  return time && time.length === 5 ? `${time}:00` : time || '';
}

/**
 * Picks the schedule timing for a visit moved to a new wall-clock window (all times "HH:mm:ss", agency-local).
 * Candidates are the visit dialog's Schedule Time options: timings that overlap the window, with overnight
 * timings (end < start) matched on either side of midnight. Among them, the one covering the MOST minutes of the
 * visit wins (e.g. 11:30–13:30 → Lunch 12:00–14:00 over Late-morning 09:00–12:00). Ties: keep currentId → the
 * timing containing the new start → the first one. Returns currentId when nothing applies.
 */
export const findScheduleTimingIdForWindow = (
  timings: Array<{ id: number; startTime: string; endTime: string }>,
  startTime: string,
  endTime: string,
  currentId: number | null
): number | null => {
  const isOvernight = (timing: { startTime: string; endTime: string }) =>
    normalizeTimeToHHmmss(timing.endTime) < normalizeTimeToHHmmss(timing.startTime);

  const windowCrossesMidnight = endTime < startTime;

  const applies = (timing: { startTime: string; endTime: string }) => {
    const timingStart = normalizeTimeToHHmmss(timing.startTime);
    const timingEnd = normalizeTimeToHHmmss(timing.endTime);

    if (windowCrossesMidnight) {
      return isOvernight(timing);
    }

    if (isOvernight(timing)) {
      return startTime >= timingStart || endTime <= timingEnd;
    }

    return timingEnd >= startTime && timingStart <= endTime;
  };

  const containsStart = (timing: { startTime: string; endTime: string }) => {
    const timingStart = normalizeTimeToHHmmss(timing.startTime);
    const timingEnd = normalizeTimeToHHmmss(timing.endTime);

    return isOvernight(timing)
      ? startTime >= timingStart || startTime < timingEnd
      : startTime >= timingStart && startTime < timingEnd;
  };

  const MINUTES_PER_DAY = 24 * 60;

  const toMinutes = (time: string) => {
    const [hours, minutes] = normalizeTimeToHHmmss(time).split(':').map(Number);

    return hours * 60 + minutes;
  };

  // Minutes of the visit window a timing covers. Both ranges are laid on a two-day timeline (an end before its
  // start is pushed past midnight) and the timing is also tried a day earlier/later to catch overnight wrap-arounds.
  const windowStart = toMinutes(startTime);
  const windowEnd = toMinutes(endTime) + (windowCrossesMidnight ? MINUTES_PER_DAY : 0);

  const overlapMinutes = (timing: { startTime: string; endTime: string }) => {
    const timingStart = toMinutes(timing.startTime);
    const timingEnd = toMinutes(timing.endTime) + (isOvernight(timing) ? MINUTES_PER_DAY : 0);

    return Math.max(
      ...[-MINUTES_PER_DAY, 0, MINUTES_PER_DAY].map(shift =>
        Math.max(0, Math.min(windowEnd, timingEnd + shift) - Math.max(windowStart, timingStart + shift))
      )
    );
  };

  const matching = timings.filter(applies);

  if (!matching.length) {
    return currentId;
  }

  const bestOverlap = Math.max(...matching.map(overlapMinutes));
  const best = matching.filter(timing => overlapMinutes(timing) === bestOverlap);

  if (currentId !== null && best.some(timing => timing.id === currentId)) {
    return currentId;
  }

  return (best.find(containsStart) ?? best[0]).id;
};

/**
 * User Time-zone strings into ISO string in UTC
 */
export function toISOString(dateOrDatetime?: string, time?: string): string {
  if (!dateOrDatetime) return '';

  const timeZone = localStorage.getItem('timeZone') || 'UTC';
  let dateTime: DateTime | null = null;

  if (time) {
    dateTime = DateTime.fromFormat(`${dateOrDatetime} ${time}`, 'yyyy-MM-dd HH:mm:ss', { zone: timeZone });
  } else {
    const formatsToTry = ['yyyy-MM-dd HH:mm:ss', 'yyyy-MM-dd HH:mm', "yyyy-MM-dd'T'HH:mm:ss", "yyyy-MM-dd'T'HH:mm"];
    for (const fmt of formatsToTry) {
      dateTime = DateTime.fromFormat(dateOrDatetime, fmt, { zone: timeZone });
      if (dateTime.isValid) break;
    }
  }

  return dateTime && dateTime.isValid ? dateTime.toUTC().toISO() || '' : '';
}

/**
 * Converts a datetime string (local format or already ISO) to UTC ISO for API payloads.
 */
export function toUTCISOString(dateOrDatetime?: string): string {
  if (!dateOrDatetime) return '';
  const fromISO = DateTime.fromISO(dateOrDatetime, { zone: 'utc' });
  if (fromISO.isValid) return fromISO.toISO() ?? '';

  return toISOString(dateOrDatetime);
}

export function mergeDateAndTimeToUTC(date: string, time: string): string {
  if (!date || !time) return '';
  const timeZone = localStorage.getItem('timeZone') || 'UTC';

  // normalize `time` like "03:27" → "03:27:00"
  const fullTime = time.length === 5 ? `${time}:00` : time;

  const dt = DateTime.fromFormat(`${date} ${fullTime}`, 'yyyy-MM-dd HH:mm:ss', { zone: timeZone });

  return dt.isValid ? dt.toUTC().toISO() || '' : '';
}

export function convertToCSV(data: any[], headers: Record<string, string>) {
  const csvRows = [];

  const headerRow = Object.values(headers).join(',');

  csvRows.push(headerRow);

  data.forEach(row => {
    const csvRow = [];
    for (const key in headers) {
      let value = row[key];

      if (typeof value === 'string' && value.includes(',')) value = `"${value}"`;

      csvRow.push(value);
    }
    csvRows.push(csvRow.join(','));
  });

  return csvRows.join('\n');
}

export function downloadCSV(csvData: string, fileName: string) {
  const blob = new Blob([csvData], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.setAttribute('hidden', '');
  a.setAttribute('href', url);
  a.setAttribute('download', fileName);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function camelToNormalCase(str: string): string {
  if (!str) return '';

  const result = str.replace(/([A-Z])/g, ' $1').trim();

  return result.charAt(0).toUpperCase() + result.slice(1);
}

export function snakeToNormalCase(str: string): string {
  if (!str) return '';

  const result = str.replace(/_/g, ' ');

  return result.charAt(0).toUpperCase() + result.slice(1).toLowerCase();
}

export function formatActivityDate(isoString: string): string {
  if (!isoString) return '';

  const timeZone = localStorage.getItem('timeZone') || 'UTC';
  const dateFormat = localStorage.getItem('dateFormat') || 'dd-MM-yyyy';
  const timeFormatSetting = JSON.parse(localStorage.getItem('timeFormat') || '{}');
  const timeFormat = timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm a';

  const date = DateTime.fromISO(isoString).setZone(timeZone);
  const now = DateTime.now().setZone(timeZone);

  if (date.hasSame(now, 'day')) {
    return `Today at ${date.toFormat(timeFormat)}`;
  }

  if (date.hasSame(now.minus({ days: 1 }), 'day')) {
    return `Yesterday at ${date.toFormat(timeFormat)}`;
  }

  return `${date.toFormat(dateFormat)} at ${date.toFormat(timeFormat)}`;
}

/**
 * Get currency symbol from agencyProfile in localStorage
 * Returns € (euro) if currency is null or not found
 */
export function getCurrencySymbol(): string {
  try {
    const agencyProfile = JSON.parse(localStorage.getItem('agencyProfile') || '{}');

    return agencyProfile?.currency?.symbol || '€';
  } catch {
    return '€';
  }
}

/**
 * Get currency icon from agencyProfile in localStorage based on currency code
 * Returns tabler-currency-euro if currency is null or not found
 */
export function getCurrencyIcon(): string {
  try {
    const agencyProfile = JSON.parse(localStorage.getItem('agencyProfile') || '{}');
    const currencyCode = agencyProfile?.currency?.code || 'EUR';

    const currencyIconMap: Record<string, string> = {
      USD: 'tabler-currency-dollar',
      EUR: 'tabler-currency-euro',
      GBP: 'tabler-currency-pound',
      JPY: 'tabler-currency-yen',
      INR: 'tabler-currency-rupee',
      CAD: 'tabler-currency-dollar-canadian',
      AUD: 'tabler-currency-dollar-australian',
      NZD: 'tabler-currency-dollar',
      CHF: 'tabler-currency-frank',
      CNY: 'tabler-currency-yuan',
      KRW: 'tabler-currency-won'
    };

    return currencyIconMap[currencyCode] || 'tabler-currency-euro';
  } catch {
    return 'tabler-currency-euro';
  }
}

/* ------------------------------ CHARGE RATE HELPERS ------------------------------ */

export const transformRateToAPI = (rate: IRateInput, index: number): IRate => {
  const dayType = rate.dayType === 'specificDays' ? 'specific_day' : rate.dayType || 'weekday';

  const applicableDays = dayType !== 'holiday' ? (rate.days as any) : undefined;

  return {
    dayType: dayType as 'weekday' | 'weekend' | 'holiday' | 'specific_day',
    applicableDays,
    scheduleTimeId: rate.scheduleTimeId ?? null,
    startTime: rate.startTime,
    endTime: rate.endTime,
    rateType: (rate.unit.toLowerCase() === 'flat' ? 'flat' : 'hourly') as 'hourly' | 'flat',
    rateAmount: Number(rate.amount) || 0,
    priorityOrder: index + 1,
    ...(dayType === 'holiday' && rate.holidayIds?.length ? { holidayIds: rate.holidayIds } : {})
  };
};

/* ------------------------------ TOAST HELPERS ------------------------------ */

/**
 * Sets the z-index of toast notifications to ensure they appear above all overlays and dialogs
 * This function finds the highest z-index among all overlays and sets the toast container
 * to a z-index that is at least 100 higher than the highest overlay
 */
export function setToastZIndex(): void {
  const toastContainer = document.querySelector('.Toastify__toast-container') as HTMLElement;
  if (!toastContainer) {
    return;
  }

  const allOverlays = document.querySelectorAll('.v-overlay__content, .v-overlay__scrim, .v-navigation-drawer');
  let maxZIndex = 0;

  allOverlays.forEach(overlay => {
    const el = overlay as HTMLElement;
    const computedStyle = getComputedStyle(el);
    const computedZIndex = parseInt(computedStyle.zIndex || '0', 10) || 0;
    const inlineZIndex = parseInt(el.style.zIndex || '0', 10) || 0;
    const actualZIndex = Math.max(computedZIndex, inlineZIndex);

    if (actualZIndex > maxZIndex) {
      maxZIndex = actualZIndex;
    }
  });

  const targetZIndex = Math.max(10002, maxZIndex + 100);

  toastContainer.style.setProperty('z-index', String(targetZIndex), 'important');
  toastContainer.style.setProperty('position', 'fixed', 'important');

  const toasts = document.querySelectorAll('.Toastify__toast');

  toasts.forEach(toast => {
    const toastEl = toast as HTMLElement;

    toastEl.style.setProperty('z-index', String(targetZIndex), 'important');
  });
}

export const copyToClipboard = (data: string): boolean => {
  try {
    navigator.clipboard.writeText(data);

    return true;
  } catch (error) {
    return false;
  }
};

export const capitalizeFirstWord = (text: string) => {
  if (!text) return '';
  text = text.trim();

  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const transformText = (input: string): string => {
  if (!input) return '';

  return input
    .split('_')
    .map(word => capitalizeFirstWord(word))
    .join(' ');
};

export type IAppointmentTimeclockStatusKey = 'missed' | 'completed' | 'delayed in progress' | 'started';

// Ground truth for whether a carer clocked in late is each timeclock's own `status` field
// (delayed_in_progress / on_time / in_progress / completed) — the appointment-level
// `timeclockStatus` aggregate the backend sometimes attaches isn't always present, and
// re-deriving delayed vs started on the frontend from raw clock-in timestamps can disagree with
// the backend's own delay window (flexibleStart handling, timezones, etc). Missed falls back to
// the frontend rule (no clock-in and both the start and end time have already passed) whenever
// the backend hasn't already flagged it. Returns null when the carer hasn't clocked in yet and
// the appointment hasn't ended, so callers can fall back to their own assigned/open logic.
export function getAppointmentTimeclockStatusKey(item: {
  timeclockStatus?: string;
  startTime?: string | null;
  endTime?: string | null;
  timeclocks?: Array<{ clockInTime?: string | null; clockOutTime?: string | null; status?: string }>;
}): IAppointmentTimeclockStatusKey | null {
  if (item.timeclockStatus === 'missed') return 'missed';

  const timeclocks = item.timeclocks ?? [];

  if (timeclocks.some(t => t.clockOutTime)) return 'completed';

  const clockedIn = timeclocks.find(t => t.clockInTime);
  if (!clockedIn) {
    const now = DateTime.utc();
    const startPassed = !!item.startTime && DateTime.fromISO(item.startTime, { zone: 'utc' }) < now;
    const endPassed = !!item.endTime && DateTime.fromISO(item.endTime, { zone: 'utc' }) < now;

    if (startPassed && endPassed) return 'missed';

    return null;
  }

  return clockedIn.status === 'delayed_in_progress' ? 'delayed in progress' : 'started';
}

// camel case to normal with capitalizeFirstWord
export const transformTextCamelToNormal = (input: string): string => {
  if (!input) return '';

  return input
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map(word => capitalizeFirstWord(word))
    .join(' ');
};
